import * as dateFns from 'date-fns';

import { AbsenceType, Exchange, TeamShift } from '@pdcplan/shared/api/TeamPlan';
import {
  EditedShift,
  ShiftDraft,
  getShiftDraftLabel,
  isNewlyCreatedShift,
} from '../ListView/CreateShift/EditedShift';
import {
  ExchangeCandidateFilterEnum,
  ExchangeTypeEnum,
  TeamShiftStatusEnum,
} from '@pdcplan/shared/enums';
import { Period } from '../util/dates';
import { ListViewTab, getSiteRoutes, getTeamRouteParams } from '../routes';
import {
  addLoadingPeriod,
  attemptLoadTeamRoster,
  loadTeamRosterFailure,
  loadTeamRosterRequest,
  loadTeamRosterSuccess,
  removeLoadingPeriod,
  updateShift,
  updateShifts,
} from '../ListView/ListViewActions';
import { closeModal, queueToast, setModalLoading } from '../appActions';
import { history, push, replace } from '../history';

import { ActiveOfferView } from './ShiftViewEnums';
import { Store } from '../rootReducer';
import { ThunkDispatch } from '../types';
import { ToastType } from '@pdcfrontendui/components/Toast/Toast';
import { currentLanguage } from '../currentLanguage';
import getApi from '../getApi';
import { getFormatter } from '../hooks/useLang';

export type ShiftViewAction =
  | {
      type: 'GETABSENCETYPES_REQUEST';
    }
  | {
      type: 'GETABSENCETYPES_SUCCESS';
      getAbsenceTypesResponse: AbsenceType[];
    }
  | {
      type: 'GETABSENCETYPES_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'REGISTERABSENCE_REQUEST';
    }
  | {
      type: 'REGISTERABSENCE_SUCCESS';
      registerAbsenceResponse: {
        teamShiftList: TeamShift[];
      };
    }
  | {
      type: 'REGISTERABSENCE_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'MARKASHANDLED_REQUEST';
    }
  | {
      type: 'MARKASHANDLED_SUCCESS';
      markAsHandledResponse: TeamShift;
      personId: number;
    }
  | {
      type: 'MARKASHANDLED_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'OFFER_ON_SHIFT_ECHANGE_REQUEST';
    }
  | {
      type: 'OFFER_ON_SHIFT_ECHANGE_SUCCESS';
      offerOnShiftExchangeResponse: TeamShift;
    }
  | {
      type: 'OFFER_ON_SHIFT_ECHANGE_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'CANCEL_OFFER_ON_SHIFT_ECHANGE_REQUEST';
    }
  | {
      type: 'CANCEL_OFFER_ON_SHIFT_ECHANGE_SUCCESS';
      cancelOfferOnShiftExchangeResponse: TeamShift;
      personId: number;
    }
  | {
      type: 'CANCEL_OFFER_ON_SHIFT_ECHANGE_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'ASSIGNSHIFT_REQUEST';
    }
  | {
      type: 'ASSIGNSHIFT_SUCCESS';
      assignShiftResponse: TeamShift[];
    }
  | {
      type: 'ASSIGNSHIFT_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'GETEXCHANGE_REQUEST';
    }
  | {
      type: 'GETEXCHANGE_SUCCESS';
      exchange: Exchange;
    }
  | {
      type: 'GETEXCHANGE_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'SET_SWAP_VIEW_OPEN';
      open: boolean;
    }
  | {
      type: 'SET_OFFER_VIEW_OPEN';
      open: boolean;
    }
  | {
      type: 'SET_ACTIVE_OFFER_VIEW';
      activeOfferView: ActiveOfferView;
    }
  | {
      type: 'EXCHANGE_ACCEPT_REQUEST';
    }
  | {
      type: 'EXCHANGE_ACCEPT_SUCCESS';
    }
  | {
      type: 'EXCHANGE_ACCEPT_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'EXCHANGE_REJECT_REQUEST';
    }
  | {
      type: 'EXCHANGE_REJECT_SUCCESS';
    }
  | {
      type: 'EXCHANGE_REJECT_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'EXCHANGE_SELECT_EXCHANGE_ID';
      exchangeId: number | undefined;
    }
  | {
      type: 'SET_SHIFT_DRAFT';
      data: ShiftDraft | null;
    }
  | {
      type: 'UPDATE_SELECTED_CANDIDATE_FILTER';
      candidateFilter: ExchangeCandidateFilterEnum;
    }
  | {
      type: 'REMOVE_SHIFT_REQUEST';
    }
  | {
      type: 'REMOVE_SHIFT_SUCCESS';
    }
  | {
      type: 'REMOVE_SHIFT_FAILURE';
      errorMessage: string;
    }
  | {
      type: 'SET_CONFIRM_DELETE_MODAL';
      shown: boolean;
    }
  | {
      type: 'EDIT_SHIFT_REQUEST';
    }
  | {
      type: 'EDIT_SHIFT_SUCCESS';
    }
  | {
      type: 'EDIT_SHIFT_FAILURE';
    }
  | {
      type: 'SET_CONFIRM_EDIT_MODAL';
      shown: boolean;
    };

export const getAbsenceTypesRequest = (): ShiftViewAction => ({
  type: 'GETABSENCETYPES_REQUEST',
});

export const getAbsenceTypesSuccess = (
  getAbsenceTypesResponse: AbsenceType[]
): ShiftViewAction => ({
  type: 'GETABSENCETYPES_SUCCESS',
  getAbsenceTypesResponse,
});

export const getAbsenceTypesFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'GETABSENCETYPES_FAILURE',
  errorMessage,
});

export const markAsHandledRequest = (): ShiftViewAction => ({
  type: 'MARKASHANDLED_REQUEST',
});

export const markAsHandledSuccess = (
  personId: number,
  markAsHandledResponse: TeamShift
): ShiftViewAction => ({
  type: 'MARKASHANDLED_SUCCESS',
  personId,
  markAsHandledResponse,
});

export const markAsHandledFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'MARKASHANDLED_FAILURE',
  errorMessage,
});
export const offerOnShiftExchangeRequest = (): ShiftViewAction => ({
  type: 'OFFER_ON_SHIFT_ECHANGE_REQUEST',
});

export const offerOnShiftExchangeSuccess = (
  offerOnShiftExchangeResponse: TeamShift
): ShiftViewAction => ({
  type: 'OFFER_ON_SHIFT_ECHANGE_SUCCESS',
  offerOnShiftExchangeResponse,
});

export const offerOnShiftExchangeFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'OFFER_ON_SHIFT_ECHANGE_FAILURE',
  errorMessage,
});
export const cancelOfferOnShiftExchangeRequest = (): ShiftViewAction => ({
  type: 'CANCEL_OFFER_ON_SHIFT_ECHANGE_REQUEST',
});

export const cancelOfferOnShiftExchangeSuccess = (
  personId: number,
  cancelOfferOnShiftExchangeResponse: TeamShift
): ShiftViewAction => ({
  type: 'CANCEL_OFFER_ON_SHIFT_ECHANGE_SUCCESS',
  personId,
  cancelOfferOnShiftExchangeResponse,
});

export const cancelOfferOnShiftExchangeFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'CANCEL_OFFER_ON_SHIFT_ECHANGE_FAILURE',
  errorMessage,
});
export const registerAbsenceRequest = (): ShiftViewAction => ({
  type: 'REGISTERABSENCE_REQUEST',
});
export const registerAbsenceSuccess = (registerAbsenceResponse: {
  teamShiftList: TeamShift[];
}): ShiftViewAction => ({
  type: 'REGISTERABSENCE_SUCCESS',
  registerAbsenceResponse,
});

export const registerAbsenceFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'REGISTERABSENCE_FAILURE',
  errorMessage,
});

export const assignShiftRequest = (): ShiftViewAction => ({
  type: 'ASSIGNSHIFT_REQUEST',
});

export const assignShiftSuccess = (
  assignShiftResponse: TeamShift[]
): ShiftViewAction => ({
  type: 'ASSIGNSHIFT_SUCCESS',
  assignShiftResponse,
});

export const assignShiftFailure = (errorMessage: string): ShiftViewAction => ({
  type: 'ASSIGNSHIFT_FAILURE',
  errorMessage,
});

export const getExchangeRequest = (): ShiftViewAction => ({
  type: 'GETEXCHANGE_REQUEST',
});

export const getExchangeSuccess = (exchange: Exchange): ShiftViewAction => ({
  type: 'GETEXCHANGE_SUCCESS',
  exchange,
});

export const getExchangeFailure = (errorMessage: string): ShiftViewAction => ({
  type: 'GETEXCHANGE_FAILURE',
  errorMessage,
});

export const setSwapViewOpen = (open: boolean) => ({
  type: 'SET_SWAP_VIEW_OPEN',
  open,
});

export const setOfferViewOpen = (open: boolean) => ({
  type: 'SET_OFFER_VIEW_OPEN',
  open,
});

export const setActiveOfferViewOpen = (activeOfferView: ActiveOfferView) => ({
  type: 'SET_ACTIVE_OFFER_VIEW',
  activeOfferView,
});

export const exchangeAcceptRequest = (): ShiftViewAction => ({
  type: 'EXCHANGE_ACCEPT_REQUEST',
});

export const exchangeAcceptSuccess = (): ShiftViewAction => ({
  type: 'EXCHANGE_ACCEPT_SUCCESS',
});

export const exchangeAcceptFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'EXCHANGE_ACCEPT_FAILURE',
  errorMessage,
});

export const exchangeRejectRequest = (): ShiftViewAction => ({
  type: 'EXCHANGE_REJECT_REQUEST',
});

export const exchangeRejectSuccess = (): ShiftViewAction => ({
  type: 'EXCHANGE_REJECT_SUCCESS',
});

export const exchangeRejectFailure = (
  errorMessage: string
): ShiftViewAction => ({
  type: 'EXCHANGE_REJECT_FAILURE',
  errorMessage,
});

export const exchangeSelectExchangeId = (
  exchangeId: number | undefined
): ShiftViewAction => ({
  type: 'EXCHANGE_SELECT_EXCHANGE_ID',
  exchangeId,
});

export const removeShiftRequest = (): ShiftViewAction => ({
  type: 'REMOVE_SHIFT_REQUEST',
});

export const removeShiftSuccess = (): ShiftViewAction => ({
  type: 'REMOVE_SHIFT_SUCCESS',
});

export const removeShiftFailure = (errorMessage: string): ShiftViewAction => ({
  type: 'REMOVE_SHIFT_FAILURE',
  errorMessage,
});

export const editShiftRequest = (): ShiftViewAction => ({
  type: 'EDIT_SHIFT_REQUEST',
});

export const editShiftSuccess = (): ShiftViewAction => ({
  type: 'EDIT_SHIFT_SUCCESS',
});

export const editShiftFailure = (): ShiftViewAction => ({
  type: 'EDIT_SHIFT_FAILURE',
});

export const setConfirmDeleteModal = (shown: boolean): ShiftViewAction => ({
  type: 'SET_CONFIRM_DELETE_MODAL',
  shown,
});

export const setShiftDraft = (
  shiftDraft: ShiftDraft | null
): ShiftViewAction => ({
  type: 'SET_SHIFT_DRAFT',
  data: shiftDraft,
});

function dispatchRegisterAbsenceToast(
  dispatch: ThunkDispatch,
  employeeName: string,
  absenceShifts: TeamShift[]
) {
  let message = '';
  // TODO: Backend returns existing absences that were not affected by the registrations.
  absenceShifts = absenceShifts.filter(
    (shift) => shift.status === TeamShiftStatusEnum.actionRequired
  );
  if (absenceShifts.length >= 2) {
    message = currentLanguage.RegisteredAbsent_2(
      employeeName,
      getFormatter().timeIntervalLong(
        absenceShifts[1]!.period.from,
        absenceShifts[1]!.period.to
      )
    );
    if (absenceShifts.length === 3) {
      message += ' ' + currentLanguage.And1OtherShift;
    } else if (absenceShifts.length > 3) {
      message +=
        ' ' + currentLanguage.AndXOtherShifts_1(absenceShifts.length - 2);
    }
  }
  if (message.length !== 0) {
    const toast: ToastType = { text: message };
    dispatch(queueToast(toast));
  }
}

function dispatchUpdateShift(
  dispatch: ThunkDispatch,
  shiftId: string,
  teamShift: TeamShift,
  teamId: string
) {
  dispatch(updateShift(shiftId, teamShift, teamId));
}

export function attemptGetAbsenceTypes(shiftId: string, teamId: string) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    // kinda hacky way to make sure we only have one getAbsenceTypes call at a time
    if (!getState().shiftViewReducer.loading) {
      dispatch(getAbsenceTypesRequest());
      return getApi()
        .getAbsenceTypes(shiftId, teamId)
        .then((json) => {
          dispatch(getAbsenceTypesSuccess(json));
        })
        .catch((err: Error) => {
          dispatch(getAbsenceTypesFailure(err.message));
        });
    }
  };
}

export function attemptMarkAsHandled(
  teamId: string,
  personId: number,
  shiftId: string
) {
  return (dispatch: ThunkDispatch) => {
    dispatch(markAsHandledRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .markAsHandled(shiftId, teamId)
      .then((json) => {
        dispatch(markAsHandledSuccess(personId, json));
        dispatch(setModalLoading(false));
        dispatchUpdateShift(dispatch, shiftId, json, teamId);
        dispatch(closeModal());
      })
      .catch((err: Error) => {
        dispatch(markAsHandledFailure(err.message));
        dispatch(setModalLoading(false));
        dispatch(closeModal());
      });
  };
}
export function attemptOfferOnShiftExchange(
  shiftId: string,
  teamIdList: string[],
  currentTeamId: string,
  candidateFilter: ExchangeCandidateFilterEnum,
  remark: string
) {
  return (dispatch: ThunkDispatch) => {
    dispatch(offerOnShiftExchangeRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .offerOnShiftExchange(shiftId, teamIdList, candidateFilter, remark)
      .then((json) => {
        dispatch(offerOnShiftExchangeSuccess(json));
        dispatch(setModalLoading(false));
        dispatchUpdateShift(dispatch, shiftId, json, currentTeamId);
        dispatch(setActiveOfferViewOpen(ActiveOfferView.None));
        dispatch(closeModal());
      })
      .catch((err: Error) => {
        dispatch(offerOnShiftExchangeFailure(err.message));
        dispatch(setModalLoading(false));
        dispatch(closeModal());
      });
  };
}
export function attemptCancelOfferOnShiftExchange(
  teamId: string,
  personId: number,
  shiftId: string
) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(cancelOfferOnShiftExchangeRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .cancelOfferOnShiftExchange(shiftId, teamId)
      .then((teamShift) => {
        dispatch(cancelOfferOnShiftExchangeSuccess(personId, teamShift));
        dispatch(setModalLoading(false));
        dispatchUpdateShift(dispatch, shiftId, teamShift, teamId);
        dispatch(closeModal());
        const state = getState();
        if (!state.appReducer.screensizeBig) {
          const currentTeamRoute = getSiteRoutes().team(
            state.listViewReducer.currentTeam
          );
          push(currentTeamRoute);
        }
      })
      .catch((err: Error) => {
        dispatch(cancelOfferOnShiftExchangeFailure(err.message));
        dispatch(setModalLoading(false));
        dispatch(closeModal());
      });
  };
}

export function attemptAssignShift(
  teamId: string,
  personId: number,
  shiftId: string,
  updatedPeriod: Period | null,
  currentlyOnFictive: boolean
) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(assignShiftRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .assignShift(teamId, personId, shiftId, updatedPeriod)
      .then((teamShiftList) => {
        // Important to reset shiftDraft before any other state changes
        dispatch(setShiftDraft(null));
        dispatch(assignShiftSuccess(teamShiftList));
        dispatch(setModalLoading(false));
        dispatch(updateShifts(shiftId, teamShiftList, teamId));
        dispatch(closeModal());

        // "assignShift/assignCallInList" copies the shift to a new person and marks the original shift as handled.
        // For fictive persons, we prefer to remove the original shift.
        if (currentlyOnFictive) {
          void dispatch(attemptRemoveShift(shiftId, teamId));
        }

        const state = getState();
        const params = getTeamRouteParams(history.location);
        if (!state.appReducer.screensizeBig) {
          const currentTeamRoute = getSiteRoutes().team(
            state.listViewReducer.currentTeam
          );
          push(currentTeamRoute);
        } else {
          // Exit the find substitute state, and show the shift in the assigned state. But stay on the pending tab (if not prviously on fictive).
          const newShiftId = teamShiftList[1]?.id;
          if (newShiftId) {
            push(
              getSiteRoutes().team(teamId, {
                personId,
                shiftId: newShiftId,
                // tab: !currentlyOnFictive ? Tab.Pending : Tab.Planned,
                tab: params.tab,
              })
            );
          }
        }
      })
      .catch((err: Error) => {
        dispatch(assignShiftFailure(err.message));
        dispatch(setModalLoading(false));
        dispatch(closeModal());
      });
  };
}

export function attemptAssignShiftCandidate(teamId: string, personId: number) {
  return async (dispatch: ThunkDispatch, getState: () => Store) => {
    try {
      const shiftDraft = getState().shiftViewReducer.shiftDraft;
      const assignedTo =
        getState().listViewReducer.employeeMap[teamId]?.[personId];
      if (!shiftDraft || isNewlyCreatedShift(shiftDraft) || !assignedTo) {
        return;
      }
      dispatch(setModalLoading(true));
      const teamShift = await getApi().assignShiftCandidate(
        teamId,
        personId,
        shiftDraft
      );
      dispatch(setShiftDraft(null));
      dispatch(assignShiftSuccess([teamShift]));
      dispatch(setModalLoading(false));
      dispatch(updateShifts(teamShift.id, [teamShift], teamId));
      dispatch(closeModal());
      const label = getShiftDraftLabel(shiftDraft);
      dispatch(
        queueToast({
          text: currentLanguage.IsNowAssigned_2(
            `"${label}" ${getFormatter().timeIntervalLong(teamShift.period.from, teamShift.period.to)}`,
            assignedTo.name
          ),
        })
      );
      const state = getState();
      // Show list of planned shifts (where the assigned shift now resides)
      // But if its assigned to a fictive employee, select the shift (as user likely wants to do further handling right away)
      push(
        getSiteRoutes().team(
          teamId,
          assignedTo.isFictive || state.appReducer.screensizeBig
            ? {
                personId: teamShift.personId,
                shiftId: teamShift.id,
              }
            : undefined
        )
      );
    } catch (err) {
      if (err instanceof Error) {
        dispatch(assignShiftFailure(err.message));
        dispatch(setModalLoading(false));
        dispatch(closeModal());
      }
    }
  };
}

export function attemptRegisterAbsence(
  teamId: string,
  shiftId: string,
  absenceTypeId: string,
  returnDate?: Date | null
) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(registerAbsenceRequest());
    return getApi()
      .registerAbsence(
        shiftId,
        teamId,
        absenceTypeId,
        returnDate
          ? dateFns.subMinutes(returnDate, returnDate.getTimezoneOffset())
          : null
      )
      .then((teamShiftList) => {
        const shift = getState().listViewReducer.shiftMap[teamId]?.[shiftId];
        if (shift) {
          const employeeName =
            getState().listViewReducer.employeeMap[teamId]?.[shift.personId]
              ?.name;
          if (employeeName) {
            dispatchRegisterAbsenceToast(dispatch, employeeName, teamShiftList);
          }
        }
        dispatch(registerAbsenceSuccess({ teamShiftList }));
        dispatch(updateShifts(shiftId, teamShiftList, teamId));
        // Keep shift selected, but switch to the pending tab (where it now resides)
        replace(
          getSiteRoutes().team(teamId, {
            ...getTeamRouteParams(history.location),
            tab: ListViewTab.Pending,
          })
        );

        // This definitely isnt the best solution to reload the shifts after register absence success
        // But everything else has failed, and this should work
        const requestPeriod = getState().listViewReducer.period;

        dispatch(addLoadingPeriod(requestPeriod));
        dispatch(loadTeamRosterRequest(teamId));
        return getApi()
          .getTeamRoster(teamId, requestPeriod, false)
          .then((teamRoster) => {
            dispatch(
              loadTeamRosterSuccess(
                teamRoster.shiftMap,
                teamRoster.employeeMap,
                teamId
              )
            );
            dispatch(removeLoadingPeriod(requestPeriod));
          })
          .catch((err) => {
            if (err instanceof Error) {
              dispatch(loadTeamRosterFailure(err.message, teamId));
            }
          });
      })
      .catch((err) => {
        if (err instanceof Error) {
          dispatch(registerAbsenceFailure(err.message));
        }
      });
  };
}

export function attemptGetExchange(personId: number, shiftStart: Date) {
  return (dispatch: ThunkDispatch) => {
    dispatch(getExchangeRequest());
    return getApi()
      .getExchange(personId, shiftStart)
      .then((exchange) => {
        dispatch(getExchangeSuccess(exchange));
      })
      .catch((err: Error) => {
        dispatch(getExchangeFailure(err.message));
      });
  };
}
export function attemptExchangeAccept(
  unitid: string,
  requestExchangePeriod: Period,
  offerPersonId: number,
  offerExchangeId: number,
  requestPersonId: number | null
) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(exchangeAcceptRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .exchangeAccept(
        unitid,
        requestExchangePeriod,
        offerPersonId,
        offerExchangeId,
        requestPersonId
      )
      .then(async () => {
        dispatch(exchangeAcceptSuccess());
        await dispatch(
          attemptLoadTeamRoster(
            getState().listViewReducer.currentTeam,
            getState().listViewReducer.period,
            false,
            false
          )
        );

        push(
          getSiteRoutes().team(getState().listViewReducer.currentTeam, {
            ...getTeamRouteParams(history.location),
            shiftId: undefined,
          })
        );
        dispatch(closeModal());
      })
      .catch((err: Error) => {
        dispatch(exchangeAcceptFailure(err.message));
      })
      .finally(() => {
        dispatch(setModalLoading(false));
      });
  };
}

export function attemptExchangeReject(
  exchangeId: number,
  exchangePeriod: Period,
  exchangeUnitId: string,
  exchangeType: ExchangeTypeEnum,
  remark: string | null
) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(exchangeRejectRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .exchangeReject(
        exchangeId,
        exchangePeriod,
        exchangeUnitId,
        exchangeType,
        remark
      )
      .then(async () => {
        dispatch(exchangeRejectSuccess());
        await dispatch(
          attemptLoadTeamRoster(
            getState().listViewReducer.currentTeam,
            getState().listViewReducer.period,
            false,
            false
          )
        );

        push(
          getSiteRoutes().team(getState().listViewReducer.currentTeam, {
            ...getTeamRouteParams(history.location),
            shiftId: undefined,
          })
        );
      })
      .catch((err: Error) => {
        dispatch(exchangeRejectFailure(err.message));
      })
      .finally(() => {
        dispatch(setModalLoading(false));
      });
  };
}

export function attemptRemoveShift(
  shiftId: string,
  teamId: string,
  employeeName?: string,
  shiftLabel?: string,
  shiftTimeLabel?: string
) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(removeShiftRequest());
    dispatch(setModalLoading(true));
    return getApi()
      .removeShift(shiftId, teamId)
      .then(async () => {
        dispatch(setShiftDraft(null));
        dispatch(removeShiftSuccess());
        if (employeeName && shiftLabel && shiftTimeLabel) {
          dispatch(
            queueToast({
              text: currentLanguage.RemoveShiftToast_3(
                employeeName,
                shiftLabel,
                shiftTimeLabel
              ),
            })
          );
        }
        await dispatch(
          attemptLoadTeamRoster(
            getState().listViewReducer.currentTeam,
            getState().listViewReducer.period,
            false,
            false
          )
        );

        dispatch(closeModal());
      })
      .catch((err: Error) => {
        dispatch(removeShiftFailure(err.message));
      })
      .finally(() => {
        dispatch(setModalLoading(false));
      });
  };
}

export function attemptEndAbsence(teamId: string, shift: TeamShift) {
  return async (dispatch: ThunkDispatch, getState: () => Store) => {
    try {
      const teamShiftList = await getApi().endAbsence(shift.id, teamId);
      dispatch(updateShifts(shift.id, teamShiftList, teamId));
      const employeeName =
        getState().listViewReducer.employeeMap[teamId]?.[shift.personId]?.name;
      if (employeeName) {
        const restored = teamShiftList.filter(
          (shift) => shift.status === TeamShiftStatusEnum.planned
        );
        if (restored.length >= 2) {
          let text = currentLanguage.EndAbsenceMultiPleaseNote_2(
            employeeName,
            getFormatter().timeIntervalLong(
              restored[1]!.period.from,
              restored[1]!.period.to
            )
          );
          if (restored.length === 3) {
            text += ` ${currentLanguage.And1OtherShift}`;
          } else if (restored.length > 3) {
            text += ` ${currentLanguage.AndXOtherShifts_1(
              restored.length - 2
            )}`;
          }
          dispatch(queueToast({ text }));
        }
      }
    } catch (err) {
      //
    }
  };
}

export function attemptEditShift(teamId: string, shift: EditedShift) {
  return async (dispatch: ThunkDispatch) => {
    try {
      dispatch(editShiftRequest());
      const teamShift = await getApi().editShift(
        teamId,
        shift.originalShift,
        shift
      );
      dispatch(updateShift(shift.originalShift.id, teamShift, teamId));
      dispatch(editShiftSuccess());
      const label =
        shift.def && shift.def.id !== shift.originalShift.shiftDefId
          ? shift.def.label
          : shift.originalShift.label;
      dispatch(
        queueToast({
          text: currentLanguage.ChangedToXHasBeenSaved_1(
            `${label} ${getFormatter().timeIntervalLong(shift.from, shift.to)}`
          ),
        })
      );
      return teamShift;
    } catch (err) {
      dispatch(editShiftFailure());
    }
  };
}

